import '../../css/agent-widget.css';
import { t } from '../i18n/index.js';
import { localeHeaders } from '../services/api.js';

/**
 * Agent Widget — shared floating AI chat for every ISG app.
 *
 * Talks to the standard `isg_common.agents` framework endpoints:
 *   POST /api/agent/stream         — SSE token stream
 *   POST /api/agent/confirm/:token — replay a destructive tool
 *
 * Features:
 * - Streaming: text chunks appear as they arrive (fetch + ReadableStream).
 * - Embedded navigation: tool results that include a `nav` block are
 *   rendered as inline chips that navigate the underlying app via
 *   pushState + popstate — the widget never dismisses.
 * - Two-phase confirm: destructive tools render Confirm / Cancel
 *   buttons; Confirm POSTs the token to /api/agent/confirm/:token.
 * - Persistent: message history is stored in sessionStorage so refresh
 *   and navigation preserve the thread.
 * - Markdown: headings, bold, inline code, lists, horizontal rule,
 *   links. Fenced code blocks render but apps can opt out via scope.
 *
 * Usage:
 *
 *   import { renderAgentWidget } from '@isg/ui-kit';
 *
 *   renderAgentWidget(mountEl, {
 *     appSlug: 'helpdesk',
 *     title: 'Helpdesk AI',
 *     placeholder: 'Ask about tickets, FAQs, or users…',
 *     suggestions: [
 *       'What tickets are overdue?',
 *       'Create FAQ for password reset',
 *     ],
 *   });
 *
 * The JWT is read from localStorage.jwt_token automatically.
 */

// A function, not a module-scope array: t() must run after the locale is set.
const defaultSuggestions = () => [t('agent.default_suggestion')];

// ── Markdown renderer ────────────────────────────────────────────────

function renderContent(text) {
  const lines = (text || '').split('\n');
  const parts = [];
  let inCodeBlock = false;
  let codeLines = [];
  let inUl = false;
  let inOl = false;

  function closeList() {
    if (inUl) { parts.push('</ul>'); inUl = false; }
    if (inOl) { parts.push('</ol>'); inOl = false; }
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.trimStart().startsWith('```')) {
      if (inCodeBlock) {
        parts.push(`<pre style="background:#1e1e2e;color:#cdd6f4;padding:0.75rem;border-radius:0.375rem;overflow-x:auto;font-size:0.8rem;margin:0.5rem 0;">${esc(codeLines.join('\n'))}</pre>`);
        codeLines = [];
        inCodeBlock = false;
      } else {
        closeList();
        inCodeBlock = true;
      }
      continue;
    }
    if (inCodeBlock) { codeLines.push(line); continue; }
    if (line.trim() === '---') { closeList(); parts.push('<hr style="border:none;border-top:1px solid #e5e7eb;margin:0.5rem 0;">'); continue; }
    if (line.startsWith('### ')) { closeList(); parts.push(`<h4 style="font-size:0.85rem;font-weight:600;margin:0.5rem 0 0.25rem;">${inlineMarkdown(line.slice(4))}</h4>`); continue; }
    if (line.startsWith('## '))  { closeList(); parts.push(`<h3 style="font-size:0.95rem;font-weight:600;margin:0.5rem 0 0.25rem;">${inlineMarkdown(line.slice(3))}</h3>`); continue; }
    if (/^[-*] /.test(line.trimStart())) {
      if (inOl) { parts.push('</ol>'); inOl = false; }
      if (!inUl) { parts.push('<ul style="margin:0.25rem 0;padding-left:1.25rem;">'); inUl = true; }
      parts.push(`<li style="margin:0.125rem 0;">${inlineMarkdown(line.trimStart().replace(/^[-*] /, ''))}</li>`);
      continue;
    }
    if (/^\d+\.\s/.test(line.trimStart())) {
      if (inUl) { parts.push('</ul>'); inUl = false; }
      if (!inOl) { parts.push('<ol style="margin:0.25rem 0;padding-left:1.25rem;">'); inOl = true; }
      parts.push(`<li style="margin:0.125rem 0;">${inlineMarkdown(line.trimStart().replace(/^\d+\.\s/, ''))}</li>`);
      continue;
    }
    if (line.trim() === '') { closeList(); continue; }
    closeList();
    parts.push(`<p style="margin:0.25rem 0;">${inlineMarkdown(line)}</p>`);
  }
  if (inCodeBlock && codeLines.length) {
    parts.push(`<pre style="background:#1e1e2e;color:#cdd6f4;padding:0.75rem;border-radius:0.375rem;overflow-x:auto;font-size:0.8rem;margin:0.5rem 0;">${esc(codeLines.join('\n'))}</pre>`);
  }
  closeList();
  return parts.join('');
}

function inlineMarkdown(text) {
  let out = esc(text);
  out = out.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  out = out.replace(/`([^`]+)`/g, '<code style="background:#f3f4f6;padding:0.1rem 0.3rem;border-radius:0.25rem;font-size:0.8rem;">$1</code>');
  // Inline markdown links — only relative or https:// accepted.
  out = out.replace(
    /\[([^\]]+)\]\((\/[^)]*|https:\/\/[^)]*)\)/g,
    (_, label, url) => `<a href="${esc(url)}" data-nav="${esc(url)}" style="color:#3b82f6;text-decoration:underline;">${esc(label)}</a>`,
  );
  return out;
}

function esc(str) {
  const el = document.createElement('span');
  el.textContent = str || '';
  return el.innerHTML;
}

// ── Icons ────────────────────────────────────────────────────────────

const sparklesIcon = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l1.912 5.813a2 2 0 0 0 1.275 1.275L21 12l-5.813 1.912a2 2 0 0 0-1.275 1.275L12 21l-1.912-5.813a2 2 0 0 0-1.275-1.275L3 12l5.813-1.912a2 2 0 0 0 1.275-1.275L12 3z"/></svg>`;
const sendIcon = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>`;
const closeIcon = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`;
const trashIcon = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>`;
const paperclipIcon = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>`;
const fileIcon = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/><polyline points="13 2 13 9 20 9"/></svg>`;
const xIcon = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`;

// ── Auth helpers ─────────────────────────────────────────────────────
// Phase B moved the JWT into an httpOnly cookie paired with a
// csrf_access_token cookie. We send the cookie via `credentials:
// 'include'` and mirror the CSRF cookie into the X-CSRF-TOKEN header on
// state-changing requests. The legacy localStorage Authorization header
// stays as a fallback for any client that hasn't migrated yet.

function _readCookie(name) {
  if (typeof document === 'undefined') return '';
  const prefix = `${name}=`;
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) return decodeURIComponent(trimmed.slice(prefix.length));
  }
  return '';
}

function _authHeaders(token, { csrf = true } = {}) {
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (csrf) {
    const csrfToken = _readCookie('csrf_access_token');
    if (csrfToken) headers['X-CSRF-TOKEN'] = csrfToken;
  }
  return headers;
}

// ── SSE parser ───────────────────────────────────────────────────────

async function* streamAgent(url, body, token, { onAbort } = {}) {
  const controller = new AbortController();
  if (onAbort) onAbort(controller);

  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream',
      ..._authHeaders(token),
      ...localeHeaders(url),
    },
    body: JSON.stringify(body),
    signal: controller.signal,
  });

  if (!res.ok || !res.body) {
    const text = await res.text().catch(() => '');
    throw new Error(t('agent.stream_failed_status', { status: res.status, detail: text.slice(0, 200) }));
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let boundary;
    while ((boundary = buffer.indexOf('\n\n')) !== -1) {
      const frame = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      const parsed = parseSSEFrame(frame);
      if (parsed) yield parsed;
    }
  }
}

function parseSSEFrame(frame) {
  let event = 'message';
  let data = '';
  for (const line of frame.split('\n')) {
    if (line.startsWith('event: ')) event = line.slice(7).trim();
    else if (line.startsWith('data: ')) data += line.slice(6);
  }
  if (!data) return null;
  try { return { event, data: JSON.parse(data) }; }
  catch { return { event, data: { raw: data } }; }
}

// ── Main export ──────────────────────────────────────────────────────

/**
 * Mount an agent widget into `container`.
 *
 * @param {HTMLElement} container - DOM node to render into.
 * @param {object} options
 * @param {string} options.appSlug - Used to namespace sessionStorage
 *   so multiple apps keep separate conversations on the same host.
 * @param {string} [options.title='AI Assistant'] - Shown in the panel header.
 * @param {string} [options.placeholder='Ask a question…'] - Textarea placeholder.
 * @param {string[]} [options.suggestions] - Initial prompt chips.
 * @param {string} [options.streamUrl='/api/agent/stream']
 * @param {string} [options.confirmUrl='/api/agent/confirm']  - Token is appended.
 * @param {boolean} [options.attachments=true] - false hides the paperclip.
 */
export function renderAgentWidget(container, options = {}) {
  const appSlug = options.appSlug || 'default';
  const title = options.title || t('agent.default_title');
  const placeholder = options.placeholder || t('agent.ask_placeholder');
  const suggestions = (options.suggestions && options.suggestions.length)
    ? options.suggestions : defaultSuggestions();
  const streamUrl = options.streamUrl || '/api/agent/stream';
  const confirmUrlBase = options.confirmUrl || '/api/agent/confirm';
  const uploadUrl = options.uploadUrl || '/api/agent/upload';
  // An assistant with no upload route (a local-model one) must not offer the
  // paperclip: a button whose upload 404s reads as the feature being broken.
  const allowAttachments = options.attachments !== false;
  const storageKey = `isg-agent-chat-${appSlug}`;

  const MAX_ATTACHMENTS = 5;
  const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
  const ACCEPTED_MIMES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];

  let open = false;
  let input = '';
  let sending = false;
  let activeController = null;
  let messages = loadMessages();
  // Attachments staged for the next send. Each item:
  //   { file_id, name, type, size }  after successful upload, OR
  //   { localId, name, type, size, uploading: true, error?: string } while pending
  let pendingAttachments = [];
  let attachmentError = '';
  // Seed id counter above any ids already in persisted messages so new
  // bubbles never collide with existing ones (collisions caused streamed
  // chunks to update the wrong bubble).
  let nextId = messages.reduce((max, m) => Math.max(max, Number(m.id) || 0), 0) + 1;

  function persist() {
    try { sessionStorage.setItem(storageKey, JSON.stringify(messages)); }
    catch { /* quota or disabled */ }
  }
  function loadMessages() {
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  }
  function getToken() {
    try { return localStorage.getItem('jwt_token') || ''; }
    catch { return ''; }
  }

  function renderMessage(m) {
    if (m.role === 'user') {
      const atts = (m.attachments || []).map(a => `
        <div style="display:inline-flex;align-items:center;gap:0.25rem;padding:0.15rem 0.5rem;margin:0.25rem 0.25rem 0 0;background:rgba(255,255,255,0.2);border-radius:0.25rem;font-size:0.7rem;">
          ${fileIcon}<span>${esc(a.name)}</span>
        </div>
      `).join('');
      const body = m.content ? `<p style="margin:0;">${esc(m.content)}</p>` : '';
      return `<div class="ai-msg user" data-msg-id="${m.id}">${body}${atts ? `<div style="margin-top:${m.content ? '0.25rem' : '0'};">${atts}</div>` : ''}</div>`;
    }
    const hasContent = !!(m.content && m.content.trim());
    const hasExtras = (m.navs && m.navs.length) || (m.confirms && m.confirms.length) || m.blocked;
    const thinking = !hasContent && !hasExtras
      ? `<div class="ai-thinking" style="display:flex;gap:0.25rem;padding:0.25rem 0;"><span class="ai-dot" style="animation-delay:0s;"></span><span class="ai-dot" style="animation-delay:0.15s;"></span><span class="ai-dot" style="animation-delay:0.3s;"></span></div>`
      : '';
    const body = hasContent ? renderContent(m.content) : '';
    const navs = (m.navs || []).map(n =>
      `<a href="${esc(n.url)}" data-nav="${esc(n.url)}"
         style="display:inline-flex;align-items:center;gap:0.25rem;padding:0.25rem 0.625rem;margin:0.25rem 0.25rem 0 0;background:#eff6ff;color:#1d4ed8;border-radius:0.375rem;font-size:0.8rem;text-decoration:none;border:1px solid #bfdbfe;">
         ${esc(n.label)} →</a>`
    ).join('');
    const confirms = (m.confirms || []).filter(c => !c.resolved).map(c => `
      <div data-confirm="${esc(c.token)}" style="margin-top:0.5rem;padding:0.5rem;background:#fff7ed;border:1px solid #fdba74;border-radius:0.375rem;font-size:0.85rem;">
        <div style="margin-bottom:0.375rem;color:#9a3412;">${esc(c.summary)}</div>
        <button data-confirm-btn="${esc(c.token)}" class="ai-confirm-btn" style="padding:0.25rem 0.75rem;margin-right:0.375rem;background:#dc2626;color:white;border:none;border-radius:0.25rem;font-size:0.8rem;cursor:pointer;">${esc(t('common.confirm'))}</button>
        <button data-cancel-btn="${esc(c.token)}" style="padding:0.25rem 0.75rem;background:#f3f4f6;color:#374151;border:1px solid #d1d5db;border-radius:0.25rem;font-size:0.8rem;cursor:pointer;">${esc(t('common.cancel'))}</button>
      </div>
    `).join('');
    const blockedBadge = m.blocked
      ? `<div style="margin-top:0.375rem;font-size:0.7rem;color:#92400e;background:#fef3c7;padding:0.125rem 0.5rem;border-radius:0.25rem;display:inline-block;">${esc(m.blockReason || t('agent.out_of_scope'))}</div>`
      : '';
    return `<div class="ai-msg assistant" data-msg-id="${m.id}">${thinking}${body}${navs ? `<div style="margin-top:0.5rem;">${navs}</div>` : ''}${confirms}${blockedBadge}</div>`;
  }

  function renderAttachmentTray() {
    if (pendingAttachments.length === 0 && !attachmentError) return '';
    const chips = pendingAttachments.map(a => {
      const isUploading = !!a.uploading;
      const hasError = !!a.error;
      const bg = hasError ? '#fee2e2' : (isUploading ? '#f3f4f6' : '#eff6ff');
      const border = hasError ? '#fca5a5' : (isUploading ? '#e5e7eb' : '#bfdbfe');
      const color = hasError ? '#991b1b' : '#1d4ed8';
      const status = isUploading ? ` (${esc(t('agent.uploading'))})` : (hasError ? ` (${esc(a.error)})` : '');
      const key = a.file_id || a.localId;
      return `
        <div style="display:inline-flex;align-items:center;gap:0.375rem;padding:0.25rem 0.5rem;background:${bg};border:1px solid ${border};border-radius:0.375rem;font-size:0.75rem;color:${color};max-width:100%;">
          ${fileIcon}
          <span style="max-width:12rem;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${esc(a.name)}${status}</span>
          <button data-remove-att="${esc(key)}" title="${esc(t('common.remove'))}" style="background:none;border:none;cursor:pointer;color:inherit;padding:0;display:flex;align-items:center;">${xIcon}</button>
        </div>
      `;
    }).join('');
    const err = attachmentError
      ? `<div style="color:#991b1b;font-size:0.7rem;margin-top:0.25rem;">${esc(attachmentError)}</div>`
      : '';
    return `<div class="ai-attachment-tray" style="padding:0.375rem 0.75rem 0;display:flex;flex-wrap:wrap;gap:0.375rem;">${chips}${err}</div>`;
  }

  function canSend() {
    if (sending) return false;
    if (pendingAttachments.some(a => a.uploading)) return false;
    const hasAttachments = pendingAttachments.some(a => a.file_id);
    return !!input.trim() || hasAttachments;
  }

  function handleFilesSelected(fileList) {
    attachmentError = '';
    const files = Array.from(fileList || []);
    const remaining = MAX_ATTACHMENTS - pendingAttachments.length;
    if (files.length > remaining) {
      attachmentError = t('agent.too_many_files', { count: MAX_ATTACHMENTS });
    }
    const toUpload = files.slice(0, remaining);
    for (const file of toUpload) {
      if (!ACCEPTED_MIMES.includes(file.type)) {
        attachmentError = t('agent.unsupported_type', { name: file.name });
        continue;
      }
      if (file.size > MAX_ATTACHMENT_BYTES) {
        attachmentError = t('agent.too_large', { name: file.name, max: '10 MB' });
        continue;
      }
      const localId = `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      pendingAttachments.push({
        localId,
        name: file.name,
        type: file.type,
        size: file.size,
        uploading: true,
      });
      uploadAttachment(localId, file);
    }
    render();
  }

  async function uploadAttachment(localId, file) {
    const fd = new FormData();
    fd.append('file', file);
    const token = getToken();
    try {
      const res = await fetch(uploadUrl, {
        method: 'POST',
        credentials: 'include',
        headers: { ..._authHeaders(token), ...localeHeaders(uploadUrl) },
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      const entry = pendingAttachments.find(a => a.localId === localId);
      if (!entry) return;
      if (!res.ok) {
        entry.uploading = false;
        entry.error = data.error || t('agent.upload_failed_status', { status: res.status });
      } else {
        entry.uploading = false;
        entry.file_id = data.file_id;
        entry.name = data.name || entry.name;
        entry.type = data.type || entry.type;
        entry.size = data.size || entry.size;
        delete entry.localId;
      }
    } catch (err) {
      const entry = pendingAttachments.find(a => a.localId === localId);
      if (entry) { entry.uploading = false; entry.error = err.message || t('agent.upload_failed'); }
    } finally {
      render();
    }
  }

  function removeAttachment(key) {
    pendingAttachments = pendingAttachments.filter(a => (a.file_id || a.localId) !== key);
    attachmentError = '';
    render();
  }

  function render() {
    container.innerHTML = `
      ${open ? `
        <div class="ai-panel">
          <div class="ai-panel-header">
            <div style="display:flex;align-items:center;gap:0.5rem;">
              ${sparklesIcon}
              <span style="font-weight:600;">${esc(title)}</span>
            </div>
            <div style="display:flex;align-items:center;gap:0.25rem;">
              <button class="ai-header-btn" id="ai-clear" title="${esc(t('common.clear'))}" style="background:none;border:none;color:white;cursor:pointer;padding:0.25rem;border-radius:0.25rem;opacity:0.8;">${trashIcon}</button>
              <button class="ai-header-btn" id="ai-close" title="${esc(t('common.close'))}" style="background:none;border:none;color:white;cursor:pointer;padding:0.25rem;border-radius:0.25rem;opacity:0.8;">${closeIcon}</button>
            </div>
          </div>
          <div class="ai-messages" id="ai-messages">
            ${messages.length === 0 ? `
              <div style="padding:1rem;text-align:center;">
                <p style="color:#6b7280;font-size:0.85rem;margin-bottom:0.75rem;">${esc(t('agent.how_can_i_help'))}</p>
                <div style="display:flex;flex-wrap:wrap;gap:0.5rem;justify-content:center;">
                  ${suggestions.map((s, i) => `<button class="ai-suggestion" data-suggestion="${i}">${esc(s)}</button>`).join('')}
                </div>
              </div>
            ` : messages.map(renderMessage).join('')}
          </div>
          ${renderAttachmentTray()}
          <div class="ai-input-area">
            <input type="file" id="ai-file" multiple accept="image/jpeg,image/png,image/gif,image/webp,application/pdf" style="display:none;">
            ${allowAttachments ? `<button class="ai-attach-btn" id="ai-attach" title="${esc(t('agent.attach_file'))}" ${sending || pendingAttachments.length >= MAX_ATTACHMENTS ? 'disabled' : ''}>${paperclipIcon}</button>` : ''}
            <textarea id="ai-input" class="ai-textarea" placeholder="${esc(placeholder)}" rows="1"
              style="flex:1;resize:none;max-height:6rem;border:1px solid #d1d5db;border-radius:0.5rem;padding:0.5rem 0.75rem;font-size:0.85rem;font-family:inherit;outline:none;">${esc(input)}</textarea>
            <button class="ai-send-btn" id="ai-send" ${!canSend() ? 'disabled' : ''}>${sendIcon}</button>
          </div>
        </div>
      ` : ''}
      <button class="ai-fab" id="ai-fab" title="${esc(title)}">${sparklesIcon}</button>
    `;
    bind();
    if (open) {
      const msgsEl = container.querySelector('#ai-messages');
      if (msgsEl) msgsEl.scrollTop = msgsEl.scrollHeight;
      const textarea = container.querySelector('#ai-input');
      if (textarea && !sending) {
        textarea.focus();
        textarea.setSelectionRange(textarea.value.length, textarea.value.length);
      }
    }
  }

  function bind() {
    container.querySelector('#ai-fab')?.addEventListener('click', () => { open = !open; render(); });
    container.querySelector('#ai-close')?.addEventListener('click', () => { open = false; render(); });
    container.querySelector('#ai-clear')?.addEventListener('click', () => {
      if (activeController) activeController.abort();
      messages = []; input = ''; sending = false; persist(); render();
    });
    container.querySelectorAll('[data-suggestion]').forEach(btn => {
      btn.addEventListener('click', () => {
        const idx = parseInt(btn.dataset.suggestion, 10);
        input = suggestions[idx];
        sendMessage();
      });
    });
    container.querySelectorAll('[data-nav]').forEach(a => {
      a.addEventListener('click', (ev) => {
        const url = a.getAttribute('data-nav');
        if (url && url.startsWith('/')) {
          ev.preventDefault();
          window.history.pushState({}, '', url);
          window.dispatchEvent(new PopStateEvent('popstate'));
        }
      });
    });
    container.querySelectorAll('[data-confirm-btn]').forEach(btn => {
      btn.addEventListener('click', () => confirmAction(btn.getAttribute('data-confirm-btn')));
    });
    container.querySelectorAll('[data-cancel-btn]').forEach(btn => {
      btn.addEventListener('click', () => cancelConfirm(btn.getAttribute('data-cancel-btn')));
    });
    const textarea = container.querySelector('#ai-input');
    if (textarea) {
      textarea.addEventListener('input', (e) => {
        input = e.target.value;
        e.target.style.height = 'auto';
        e.target.style.height = Math.min(e.target.scrollHeight, 96) + 'px';
        const sendBtn = container.querySelector('#ai-send');
        if (sendBtn) sendBtn.disabled = !canSend();
      });
      textarea.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          if (canSend()) sendMessage();
        }
      });
    }
    container.querySelector('#ai-send')?.addEventListener('click', () => {
      if (canSend()) sendMessage();
    });
    const attachBtn = container.querySelector('#ai-attach');
    const fileInput = container.querySelector('#ai-file');
    if (attachBtn && fileInput) {
      attachBtn.addEventListener('click', () => fileInput.click());
      fileInput.addEventListener('change', () => {
        handleFilesSelected(fileInput.files);
        fileInput.value = '';
      });
    }
    container.querySelectorAll('[data-remove-att]').forEach(btn => {
      btn.addEventListener('click', () => removeAttachment(btn.getAttribute('data-remove-att')));
    });
  }

  async function sendMessage() {
    if (!canSend()) return;
    const text = input.trim();
    // Snapshot uploaded attachments so we can clear the tray before sending.
    const attachmentsToSend = pendingAttachments
      .filter(a => a.file_id)
      .map(a => ({ file_id: a.file_id, name: a.name, type: a.type }));
    messages.push({
      id: nextId++,
      role: 'user',
      content: text,
      attachments: attachmentsToSend.map(a => ({ name: a.name, type: a.type })),
    });
    input = '';
    pendingAttachments = [];
    attachmentError = '';
    sending = true;
    persist();
    render();

    const assistantMsg = { id: nextId++, role: 'assistant', content: '', navs: [], confirms: [] };
    messages.push(assistantMsg);
    render();  // materialise the empty assistant bubble so surgical updates find it

    try {
      // Strip attachment metadata from prior history — only fresh
      // uploads in the current turn are sent server-side.
      const priorForServer = messages
        .slice(0, -2)
        .filter(m => m.role === 'user' || m.role === 'assistant')
        .map(m => ({ role: m.role, content: m.content || '' }));
      for await (const { event, data } of streamAgent(
        streamUrl,
        {
          message: text,
          messages: priorForServer,
          attachments: attachmentsToSend.length ? attachmentsToSend : undefined,
        },
        getToken(),
        { onAbort: (c) => { activeController = c; } },
      )) {
        applyEvent(assistantMsg, event, data);
        updateAssistantMessage(assistantMsg);
      }
    } catch (err) {
      assistantMsg.content += `\n\n**${t('agent.error_label')}:** ${err.message || t('agent.stream_failed')}`;
      updateAssistantMessage(assistantMsg);
    } finally {
      sending = false;
      activeController = null;
      persist();
      render();
    }
  }

  function updateAssistantMessage(msg) {
    const msgsEl = container.querySelector('#ai-messages');
    if (!msgsEl) { render(); return; }
    const existing = msgsEl.querySelector(`[data-msg-id="${msg.id}"]`);
    if (!existing) { render(); return; }
    existing.outerHTML = renderMessage(msg);
    const refreshed = msgsEl.querySelector(`[data-msg-id="${msg.id}"]`);
    if (refreshed) {
      refreshed.querySelectorAll('[data-nav]').forEach(a => {
        a.addEventListener('click', (ev) => {
          const url = a.getAttribute('data-nav');
          if (url && url.startsWith('/')) {
            ev.preventDefault();
            window.history.pushState({}, '', url);
            window.dispatchEvent(new PopStateEvent('popstate'));
          }
        });
      });
      refreshed.querySelectorAll('[data-confirm-btn]').forEach(btn => {
        btn.addEventListener('click', () => confirmAction(btn.getAttribute('data-confirm-btn')));
      });
      refreshed.querySelectorAll('[data-cancel-btn]').forEach(btn => {
        btn.addEventListener('click', () => cancelConfirm(btn.getAttribute('data-cancel-btn')));
      });
    }
    msgsEl.scrollTop = msgsEl.scrollHeight;
  }

  function applyEvent(msg, event, data) {
    switch (event) {
      case 'start': return;
      case 'text': msg.content += data.text || ''; return;
      case 'nav':
        if (data.url) msg.navs.push({ url: data.url, label: data.label || t('common.open') });
        return;
      case 'confirm_pending':
        if (data.token) msg.confirms.push({
          token: data.token,
          summary: data.summary || t('agent.confirm_action'),
          resolved: false,
        });
        return;
      case 'blocked':
        msg.blocked = true;
        msg.blockReason = data.reason || 'blocked';
        if (data.text) msg.content = (msg.content || '') + data.text;
        return;
      case 'scope_violation':
        msg.blocked = true;
        msg.blockReason = data.reason || t('agent.out_of_scope');
        msg.content = data.text || msg.content;
        return;
      default: return;
    }
  }

  async function confirmAction(token) {
    const parentMsg = messages.find(m => (m.confirms || []).some(c => c.token === token));
    if (!parentMsg) return;
    const entry = parentMsg.confirms.find(c => c.token === token);
    if (entry) entry.resolved = true;

    const newAssistant = { id: nextId++, role: 'assistant', content: '', navs: [], confirms: [] };
    messages.push(newAssistant);
    sending = true;
    persist();
    render();

    try {
      const priorForServer = messages
        .slice(0, -1)
        .filter(m => m.role === 'user' || m.role === 'assistant')
        .map(m => ({ role: m.role, content: m.content || '' }));
      const confirmUrl = `${confirmUrlBase}/${encodeURIComponent(token)}`;
      const res = await fetch(confirmUrl, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ..._authHeaders(getToken()),
          ...localeHeaders(confirmUrl),
        },
        body: JSON.stringify({ messages: priorForServer }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        newAssistant.content = `**${t('agent.error_label')}:** ${body.error || t('agent.confirm_failed')}`;
      } else {
        newAssistant.content = body.text || t('agent.action_completed');
        if (body.blocked) { newAssistant.blocked = true; newAssistant.blockReason = body.reason || 'blocked'; }
      }
    } catch (err) {
      newAssistant.content = `**${t('agent.error_label')}:** ${err.message || t('common.request_failed')}`;
    } finally {
      sending = false;
      persist();
      render();
    }
  }

  function cancelConfirm(token) {
    for (const m of messages) {
      for (const c of (m.confirms || [])) {
        if (c.token === token) c.resolved = true;
      }
    }
    persist();
    render();
  }

  render();
}
